import { useState, useEffect, useCallback, type FC } from 'react';
import BasicInput from './BasicInput';
import type { DadataSuggestion } from '@api/dadata';
import './input.scss';

const EMPTY_ARRAY: string[] = [];

interface InputProps {
  classNameParent?: string;
  classNameInput?: string;
  placeHolder?: string;
  value?: string;
  field?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  onInputBlur?: (value: string, field?: string) => void;
  inputType?: string;
  isAddressError?: boolean;
  addressErrorText?: string;
  updatedBackgroung?: boolean;
  arrayErrorFields?: string[];
  onChange?: (value: string, field?: string) => void;
  onSelectDaData?: (
    suggestion: DadataSuggestion,
    coords: number[],
    dadataId: number,
    addDadata: any
  ) => void;
  isNewStyleSearch?: boolean;
  skipPhoneValidationOnChange?: boolean;
  autocomplete?: string;
}

const Input: FC<InputProps> = ({
  classNameParent,
  classNameInput,
  placeHolder,
  value,
  field,
  onFocus,
  onBlur,
  onInputBlur,
  inputType,
  isAddressError = false,
  addressErrorText = '',
  updatedBackgroung = undefined,
  arrayErrorFields = EMPTY_ARRAY,
  onChange,
  onSelectDaData,
  isNewStyleSearch,
  skipPhoneValidationOnChange,
  autocomplete
}) => {
  const [isFocus, setFocus] = useState(false);
  const [isError, setError] = useState(false);

  // Address error is fully controlled by the parent via isAddressError prop
  useEffect(() => {
    if (field === 'address') {
      setError(isAddressError);
    }
  }, [isAddressError, field]);

  const handleFocus = useCallback(() => {
    setFocus(true);
    onFocus?.();
  }, [onFocus]);

  const handleBlur = useCallback(
    (isInputError?: boolean, isNeedToCheck?: boolean) => {
      setFocus(false);
      if (isNeedToCheck) {
        setError(!!isInputError);
      }
      onBlur?.();
    },
    [onBlur]
  );

  const handleInputBlur = useCallback(
    (val: string, blurField?: string): void => {
      const isFieldWithValidation =
        blurField === 'fio' ||
        !!arrayErrorFields?.find(item => item === blurField);

      if (isFieldWithValidation) {
        setError(!val);
      }

      setFocus(false);
      onInputBlur?.(val, blurField);
    },
    [arrayErrorFields, onInputBlur]
  );

  const labelText = isAddressError ? addressErrorText : placeHolder;

  return (
    <div className={`${classNameParent ?? ''}${isError ? ' error' : ''}`}>
      {(isFocus || isError) && (
        <span
          className={
            isError
              ? `error${updatedBackgroung ? ' updatedBackgroung' : ''}`
              : `helper${updatedBackgroung ? ' updatedBackgroung' : ''}`
          }
        >
          {labelText}
        </span>
      )}
      <BasicInput
        onFocus={handleFocus}
        onBlur={handleBlur}
        field={field}
        value={value}
        placeholder={placeHolder}
        type={inputType}
        onInputBlur={handleInputBlur}
        className={classNameInput}
        onChange={onChange}
        onSelectDaData={onSelectDaData}
        isNewStyleSearch={isNewStyleSearch}
        skipPhoneValidationOnChange={skipPhoneValidationOnChange}
        autocomplete={autocomplete}
      />
    </div>
  );
};

export default Input;
